import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ROLES = ["manufacturer", "wholesaler", "retailer", "admin"];

export default function SignIn() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login } = useAuth();
  const [role, setRole] = useState(sp.get("role") || "manufacturer");
  const [mode, setMode] = useState("login"); // login | register
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [regMobile, setRegMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [sold, setSold] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [buyers, setBuyers] = useState(null);

  useEffect(() => { api.get("/stats/stock-sold").then(({ data }) => { setSold(data.value); setVisitors(data.visitors); setBuyers(data.buyers); }).catch(() => {}); }, []);

  useEffect(() => { const r = sp.get("role"); if (r && ROLES.includes(r)) setRole(r); }, [sp]);

  const routeAfterLogin = (user) => {
    if (user.role === "admin") return nav("/admin");
    if (user.must_change_password) return nav("/change-password");
    if (!user.profile_complete) return nav("/complete-profile");
    return nav("/seller");
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(role, identifier.trim(), password);
      toast.success("Welcome back");
      routeAfterLogin(user);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", { role, mobile: regMobile.trim() });
      toast.success("Registered! Your temporary password is your mobile number.");
      setMode("login");
      setIdentifier(regMobile.trim());
      setPassword("");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="relative z-10"><Logo onClick={() => nav("/")} dark /></div>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Where tile stock<br /><span className="text-primary">finds its buyer.</span></h2>
          <p className="text-white/60 mt-4 max-w-sm font-medium">List your surplus, slow-moving and discontinued tile stock. Reach trade buyers and end users across India.</p>
          {sold !== null && sold > 0 && (
            <div data-testid="stock-sold-stamp" className="mt-8">
              <div className="inline-block rotate-[-4deg] border-4 border-primary/70 rounded-2xl px-7 py-4 bg-primary/10 shadow-xl">
                <div className="font-heading text-4xl font-extrabold text-primary tracking-tight leading-none break-words">₹{sold.toLocaleString("en-IN")}/-</div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/80 mt-2">Deadstock cleared till date</div>
              </div>
              <p className="text-white/50 text-xs mt-4 max-w-xs">Worth of real stock cleared by sellers on Cleartile — join them and turn your surplus into cash.</p>
            </div>
          )}
          {visitors !== null && visitors > 0 && (
            <p data-testid="signin-visitor-count" className="text-white/70 text-sm font-semibold mt-6"><span className="text-primary font-extrabold">{visitors.toLocaleString("en-IN")}+</span> people visited the marketplace till date</p>
          )}
          {buyers !== null && buyers > 0 && (
            <p data-testid="signin-buyer-count" className="text-white/70 text-sm font-semibold mt-2"><span className="text-primary font-extrabold">{buyers.toLocaleString("en-IN")}+</span> buyers joined the marketplace</p>
          )}
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
            {[["25–100", "Products / Plan"], ["1 Year", "Validity"], ["3", "Premium Tiers"]].map(([n, l]) => (
              <div key={l}><div className="font-heading text-3xl font-extrabold text-primary">{n}</div><div className="text-xs text-white/50 font-medium">{l}</div></div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 opacity-10 grid grid-cols-8 gap-1 p-4">
          {Array.from({ length: 96 }).map((_, i) => <div key={i} className="bg-white rounded-sm aspect-square" />)}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex justify-center"><Logo onClick={() => nav("/")} /></div>
          {sold !== null && sold > 0 && (
            <div data-testid="stock-sold-stamp-mobile" className="mb-6 rounded-xl border-2 border-primary/40 bg-primary/5 px-4 py-3 text-center">
              <div className="font-heading text-2xl font-extrabold text-primary leading-none break-words">₹{sold.toLocaleString("en-IN")}/-</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mt-1">Deadstock cleared on Cleartile till date</div>
            </div>
          )}
          {visitors !== null && visitors > 0 && (
            <p data-testid="signin-visitor-count-mobile" className="lg:hidden text-center text-sm font-semibold text-muted-foreground mb-6 -mt-2"><span className="text-primary font-extrabold">{visitors.toLocaleString("en-IN")}+</span> people visited the marketplace till date</p>
          )}
          {buyers !== null && buyers > 0 && (
            <p data-testid="signin-buyer-count-mobile" className="lg:hidden text-center text-sm font-semibold text-muted-foreground mb-6 -mt-5"><span className="text-primary font-extrabold">{buyers.toLocaleString("en-IN")}+</span> buyers joined the marketplace</p>
          )}
          <h1 className="font-heading text-2xl font-extrabold">Sign in to Cleartile</h1>
          <p className="text-muted-foreground text-sm font-medium mb-6">Select your account category</p>

          <Tabs value={role} onValueChange={(v) => { setRole(v); setMode("login"); }}>
            <TabsList className="grid grid-cols-2 gap-1 h-auto p-1 mb-2">
              {ROLES.slice(0, 2).map((r) => <TabsTrigger key={r} value={r} data-testid={`role-tab-${r}`} className="capitalize text-xs">{r}</TabsTrigger>)}
            </TabsList>
            <TabsList className="grid grid-cols-2 gap-1 h-auto p-1 mb-4">
              {ROLES.slice(2).map((r) => <TabsTrigger key={r} value={r} data-testid={`role-tab-${r}`} className="capitalize text-xs">{r}</TabsTrigger>)}
            </TabsList>
          </Tabs>

          {mode === "login" ? (
            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <Label>{role === "admin" ? "Email" : "Mobile Number"}</Label>
                <Input data-testid="login-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={role === "admin" ? "admin@email.com" : "10-digit mobile"} required />
              </div>
              <div>
                <Label>Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={role === "admin" ? "Password" : "Temp password = your mobile"} required />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full font-bold h-11">
                {busy ? "Signing in…" : "Sign In"}
              </Button>
              {role !== "admin" && (
                <p className="text-center text-sm text-muted-foreground">
                  New seller?{" "}
                  <button type="button" data-testid="switch-register" onClick={() => setMode("register")} className="text-primary font-bold">Register here</button>
                </p>
              )}
            </form>
          ) : (
            <form onSubmit={doRegister} className="space-y-4">
              <div>
                <Label>Mobile Number</Label>
                <Input data-testid="register-mobile" value={regMobile} onChange={(e) => setRegMobile(e.target.value)} placeholder="10-digit mobile" required />
                <p className="text-xs text-muted-foreground mt-1">Your temporary password will be this mobile number.</p>
              </div>
              <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full font-bold h-11">
                {busy ? "Registering…" : `Register as ${role}`}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already registered?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-primary font-bold">Sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
